import React, { useRef, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  TextField,
  Box,
  Autocomplete,
} from '@mui/material';
import { Tag, useTagsStore } from '@/stores/TagsStore';
import { useTranslations } from 'next-intl';
import { TagWithDelete } from '@/components/ui/Tag';

interface TagInputDialogProps {
  open: boolean;
  onClose: () => void;
  selectedTags: Tag[];
  onTagsChange: (tags: Tag[]) => void;
}

export const TagInputDialog: React.FC<TagInputDialogProps> = ({
  open,
  onClose,
  selectedTags,
  onTagsChange,
}) => {
  const t = useTranslations('TagInputDialog');
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const { searchHistoryTags, addTag, addToHistory } = useTagsStore();

  const suggestedTags = useMemo(() => {
    const historyTags = searchHistoryTags('');
    return historyTags.filter(tag =>
      !selectedTags.find(selected => selected.id === tag.id)
    ).slice(0, 8);
  }, [searchHistoryTags, selectedTags]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      sx={{
        '.MuiDialog-container': {
          alignItems: 'flex-start',
          paddingTop: '10vh',
        },
        '.MuiDialog-paper': {
          backgroundColor: 'transparent !important',
          boxShadow: 'none !important',
          backgroundImage: 'none !important',
        },
      }}
      slotProps={{
        paper: {
          sx: {
            overflow: 'visible',
            backgroundColor: 'transparent !important',
            boxShadow: 'none !important',
            backgroundImage: 'none !important',
          }
        }
      }}
    >
      <DialogContent sx={{
        padding: 0,
        overflow: 'visible',
        backgroundColor: 'transparent !important',
        backgroundImage: 'none !important',
      }}>
        <div className="relative mb-4" style={{ overflow: 'visible' }}>
          <Box
            ref={containerRef}
            className="relative"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              padding: '12px 16px',
            }}
          >
            <Autocomplete
              ref={inputRef}
              multiple
              fullWidth
              size='medium'
              freeSolo
              slotProps={{
                paper: {
                  sx: {
                    marginTop: '16px',
                    backgroundColor: 'var(--color-task-manager-bg)',
                    borderRadius: '12px',
                    '& .MuiAutocomplete-option': {
                      color: 'var(--color-foreground)',
                    },
                  }
                },
              }}
              value={selectedTags.map(tag => ({
                label: tag.name,
                id: tag.id
              }))}
              onChange={(event, newValue) => {
                const processedTags = newValue.map(item => {
                  if (typeof item === 'string') {
                    return addTag(item);
                  } else {
                    const existingTag = selectedTags.find(tag => tag.id === item.id);
                    if (existingTag) {
                      addToHistory(existingTag);
                      return existingTag;
                    } else {
                      return addTag(item.label);
                    }
                  }
                });
                onTagsChange(processedTags);
              }}
              getOptionLabel={(option) => {
                if (typeof option === 'string') {
                  return option;
                }
                return option.label;
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="standard"
                  placeholder={t('selectOrEnterTag')}
                  sx={{
                    border: '10px solid var(--color-task-manager-bg)',
                    backgroundColor: 'var(--color-task-manager-bg)',
                    borderRadius: '12px',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
                    overflow: 'visible',
                    '& .MuiInput-root': {
                      color: 'var(--color-foreground)',
                    },
                    '& input::placeholder': {
                      color: 'var(--color-muted-foreground)',
                      opacity: 0.6,
                    },
                    '& .MuiInput-underline:before, & .MuiInput-underline:after': {
                      display: 'none',
                    },
                  }}
                  slotProps={{
                    htmlInput: {
                      ...params.inputProps,
                      maxLength: 10,
                    }
                  }}
                />
              )}
              renderValue={(value, getTagProps) => <>
                <div className="flex flex-wrap gap-2">
                  {
                    value.map((option, index) => (
                      <TagWithDelete
                        {...getTagProps({ index })}
                        key={typeof option === 'string' ? option : option.id}
                        label={typeof option === 'string' ? option : option.label}
                      />
                    ))
                  }
                </div>
              </>

              }
              options={suggestedTags.map(tag => ({
                label: tag.name,
                id: tag.id
              }))}
            />
          </Box>
        </div>
      </DialogContent>
    </Dialog>
  );
};
